import { ExclamationCircleIcon } from "@heroicons/react/24/outline"
import * as Sentry from "@sentry/react"
import React from "react"
import { Link, useRouteError } from "react-router"

const RELOAD_KEY = "chunk-reload"

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase()
    return (
      msg.includes("dynamically imported module") ||
      msg.includes("failed to fetch")
    )
  }
  return false
}

function useChunkReload(error: unknown) {
  React.useEffect(() => {
    if (!error) return

    // After a deployment, stale chunk hashes cause dynamic import failures.
    // Reload once to pick up fresh assets; use sessionStorage to avoid loops.
    if (isChunkLoadError(error)) {
      const reloaded = sessionStorage.getItem(RELOAD_KEY)
      if (!reloaded) {
        sessionStorage.setItem(RELOAD_KEY, "1")
        window.location.reload()
        return
      }
      sessionStorage.removeItem(RELOAD_KEY)
    }

    console.error(error)
    Sentry.captureException(error)
  }, [error])
}

/** Route-level error page, used as errorElement in the router. */
export function ErrorPage() {
  const error = useRouteError()
  useChunkReload(error)

  return (
    <div className="w-full">
      <div className="container mx-auto flex flex-col pt-20 sm:w-1/2">
        <ExclamationCircleIcon className="mb-2 mr-auto h-16 w-16 text-gray-300" />
        <h1 className="text-2xl font-bold leading-loose text-gray-500">
          Something went wrong
        </h1>
        <p>
          Apologies, this looks like an error on our end. <br />
          Try reloading or go{" "}
          <Link to="/" className="mt-6 font-bold text-indigo-500">
            Home
          </Link>
        </p>
      </div>
    </div>
  )
}

/** Top-level fallback for the Sentry ErrorBoundary (no router context). */
export function FallbackErrorPage() {
  return (
    <div className="w-full">
      <div className="container mx-auto flex flex-col pt-20 sm:w-1/2">
        <ExclamationCircleIcon className="mb-2 mr-auto h-16 w-16 text-gray-300" />
        <h1 className="text-2xl font-bold leading-loose text-gray-500">
          Something went wrong
        </h1>
        <p>
          Apologies, this looks like an error on our end. <br />
          Try reloading or go{" "}
          <a href="/" className="mt-6 font-bold text-indigo-500">
            Home
          </a>
        </p>
      </div>
    </div>
  )
}
